"use client";

import React from "react";
import { Phone, Mail, MapPin, LoaderCircle, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactInfo = [{
  icon: Phone,
  title: "Direct Line",
  value: "+1 (810) 534-7136",
  link: "tel:+18105347136"
}, {
  icon: Mail,
  title: "Inquiries",
  value: "tom@focuspathconsulting.com",
  link: "mailto:tom@focuspathconsulting.com"
}];
const projectTypeOptions = ["AI Strategy & Roadmap", "Process Optimization", "Agentic Solutions", "Change Management", "Digital Transformation", "Global 500 Legacy Audit", "Other"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="144630" className="flex flex-col items-center justify-center p-8 text-center bg-card/40 backdrop-blur-md border border-border/50 rounded-2xl h-full min-h-[400px]">
        <div className="size-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-6">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-4">Request Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-secondary/50 hover:bg-secondary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 bg-card/40 backdrop-blur-md p-6 md:p-8 rounded-2xl border border-border/50 shadow-2xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background/50 border-border/50 focus:border-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus:border-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" className="bg-background/50 border-border/50 focus:border-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" className="bg-background/50 border-border/50 focus:border-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="project_type" rules={{
        required: "Please select a consultation area"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Consultation Area</FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-md border border-input bg-background/50 px-3 py-1 text-base shadow-xs transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:border-destructive aria-invalid:ring-destructive/20" {...field}>
                  <option value="" disabled>Select an area of focus</option>
                  {projectTypeOptions.map((opt, i) => <option key={opt} value={opt} data-index={i} className="bg-background text-foreground">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel>Estimated Annual Budget (USD) <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 100000" className="bg-background/50 border-border/50 focus:border-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Business objectives are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Business Objectives</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your current challenges, AI maturity level, and transformation goals." className="min-h-[120px] bg-background/50 border-border/50 focus:border-secondary resize-y" {...field} />
            </FormControl>
            <FormDescription>Detailed context helps us prepare for our initial consultation.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-12 text-base font-bold group bg-secondary hover:bg-secondary/90 text-white">
        {isSubmitting ? <>
            <LoaderCircle className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Schedule Consultation
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function LetsTalkWater() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="lets-talk-strategy" className="bg-background relative min-h-screen flex items-center py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/dark_moving_water_texture.png)] bg-cover bg-center opacity-20 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-blue-950/95 to-slate-900/90" />
      </div>

      {}
      <div className="dark container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="mb-12">
              <h2 className="text-5xl md:text-7xl font-black tracking-tighter text-foreground mb-6 uppercase leading-[0.9]">
                Lead<br />
                <span className="text-secondary">The Change.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">Partner with a company with people who have transformed Global 500 giants. We bring business optimization methodology with grass roots implementation processes.</p>
            </div>

            <div className="space-y-4">
              {contactInfo.map((info, i) => <Card key={i} data-index={i} className="bg-card/20 border-border/30 backdrop-blur-sm hover:bg-card/40 transition-colors group">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="size-12 rounded-full bg-secondary/10 flex items-center justify-center shrink-0 group-hover:bg-secondary/20 transition-colors">
                      <info.icon className="size-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">{info.title}</p>
                      {info.link !== "#" ? <Link to={info.link} className="text-lg font-semibold text-foreground hover:text-secondary transition-colors">
                          {info.value}
                        </Link> : <p className="text-lg font-semibold text-foreground">{info.value}</p>}
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <FormProvider formKey="strategy_form_contact" formId="strategy_form_contact" sectionName="lets-talk-strategy" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            project_type: "",
            budget: "",
            message: ""
          }} submitText="Schedule Consultation" successMessage="Thank you for reaching out. A senior FocusPath strategist will review your objectives and contact you within 24 hours.">
              <ContactFormInner />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}